import log from '@uwm/logger';
import uamfClient, {UamfHttpError} from '@uwm/uamf';

import {
    EXPERIENCE_SCORE_ERROR_TYPES,
    INSUFFICIENT_RECORDS_ERRORS,
} from '@/consts/experienceScore/experienceScoreErrorTypes';
import {notificationTypes} from '@/consts/notificationTypes';
import {EXPERIENCE_INFERENCE_GENERATE, EXPERIENCE_INFERENCE_GET} from '@/consts/uamfMethods';
import mockDataMode from '@/mocks/mockDataMode';
import inferenceMapping from '@/services/mappers/inferenceMapping';
import uamfTokenService from '@/services/uamf/uamfTokenService';
import uwmMessageService from '@/services/uwm/uwmMessageService';
import {setCalibrationProgress, setInferenceData, setInferenceLoading} from '@/state/ducks/inference';
import {setInitializationError} from '@/state/ducks/uwm';
import {
    selectInferenceData,
    selectInferenceLoading,
    selectInferenceTimestamp,
    selectIsCalibrationActive,
} from '@/state/selectors/inference';
import {selectInsightsContext} from '@/state/selectors/insights';
import {dispatch, getState} from '@/state/store';
import type {InferenceApiResponse, MappedInference} from '@/types/inference';

const INFERENCE_RETRIES = 3;
const RETRY_DELAY_MS = 1000;
const INFERENCE_FRESHNESS_MS = 60 * 60 * 1000;

interface UamfErrorBody {
    error?: string;
    error_details?: {valid_records?: number; threshold?: number};
}

const timeout = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getErrorBody = (e: unknown): UamfErrorBody =>
    e instanceof UamfHttpError ? ((e.body as UamfErrorBody) ?? {}) : {};

const fetchInference = () => {
    if (mockDataMode.isInferenceEnabled())
        return Promise.resolve(inferenceMapping(mockDataMode.getInferenceResponse()));

    return uamfTokenService.callWithToken((token) => {
        const context = selectInsightsContext(getState());

        return uamfClient.callGet({
            endpoint: context?.uamfApiUrl ?? '',
            path: EXPERIENCE_INFERENCE_GET,
            pathParams: [context?.assetId ?? ''],
            token,
            apiKey: context?.uamfApiKey,
            isThrowError: true,
            mapping: inferenceMapping,
        }) as Promise<MappedInference | null>;
    });
};

const generateInference = () => {
    if (mockDataMode.isInferenceEnabled()) return Promise.resolve(null);

    return uamfTokenService.callWithToken((token) => {
        const context = selectInsightsContext(getState());

        return uamfClient.callPost({
            endpoint: context?.uamfApiUrl ?? '',
            path: EXPERIENCE_INFERENCE_GENERATE,
            pathParams: [context?.assetId ?? ''],
            token,
            apiKey: context?.uamfApiKey,
            isThrowError: true,
        });
    });
};

const fetchInferenceWithRetries = async (): Promise<MappedInference | null> => {
    for (let attempt = 1; attempt <= INFERENCE_RETRIES; attempt++) {
        try {
            return await fetchInference();
        } catch (e) {
            if (getErrorBody(e).error === EXPERIENCE_SCORE_ERROR_TYPES.NO_INFERENCE_RESULTS_FOUND) return null;

            log.warn(`uamfInferenceService: fetch attempt ${attempt} failed`, (e as Error).message);

            if (attempt === INFERENCE_RETRIES) throw e;

            await timeout(RETRY_DELAY_MS);
        }
    }

    return null;
};

const updateInference = (inference: MappedInference | null) => {
    if (inference?.inferenceTimestamp) dispatch(setInferenceData(inference));
};

// Generating costs a round-trip on UAMF and yields the same result within the hour, so a recent
// complete result stands as-is. A calibrating result has no score yet — always regenerate so the
// INSUFFICIENT_RECORDS error keeps the progress bar moving.
const isInferenceFresh = (inference: MappedInference | null) => {
    if (!inference?.experienceScore) return false;

    const inferredAt = Date.parse(inference.inferenceTimestamp);

    return Number.isFinite(inferredAt) && Date.now() - inferredAt < INFERENCE_FRESHNESS_MS;
};

const handleInsufficientRecords = (errorBody: UamfErrorBody) => {
    const {valid_records, threshold} = errorBody.error_details ?? {};
    const state = getState();
    const shouldUpdate = selectIsCalibrationActive(state) || !selectInferenceTimestamp(state);

    if (!shouldUpdate) return;

    dispatch(
        setCalibrationProgress({
            n_sticks_identified: valid_records ?? 0,
            min_n_experiences_required: threshold ?? 0,
        })
    );
};

const fetchFreshInference = async () => {
    try {
        await generateInference();
        updateInference(await fetchInference());
    } catch (e) {
        const errorBody = getErrorBody(e);

        if (INSUFFICIENT_RECORDS_ERRORS.includes(errorBody.error ?? '')) {
            handleInsufficientRecords(errorBody);

            return;
        }

        log.error('uamfInferenceService: fresh inference failed', (e as Error).message);
    }
};

const reportInferenceOutcome = () => {
    if (selectInferenceData(getState())) {
        dispatch(setInitializationError(null));
        uwmMessageService.sendDataSuccessMessage();

        return;
    }

    const message = 'Inference fetch failed';

    dispatch(setInitializationError(message));
    uwmMessageService.sendInitializationError(message);
};

const applyInference = (rawResponse: InferenceApiResponse) => {
    const mapped = inferenceMapping(rawResponse);

    if (!mapped?.inferenceTimestamp) {
        log.warn('uamfInferenceService: applyInference received no valid inference data');

        return;
    }

    dispatch(setInferenceData(mapped));
};

const handleExperienceInference = async ({isForced = false}: {isForced?: boolean} = {}) => {
    const state = getState();

    if (!selectInsightsContext(state) || selectInferenceLoading(state)) return;

    dispatch(setInferenceLoading(true));

    let isRefreshed = false;

    try {
        const inference = await fetchInferenceWithRetries();

        updateInference(inference);

        if (isForced || !isInferenceFresh(inference)) await fetchFreshInference();

        isRefreshed = true;
    } catch (e) {
        log.error('uamfInferenceService: inference fetch failed', (e as Error).message);
    } finally {
        dispatch(setInferenceLoading(false));
    }

    // A forced refresh runs on an already-rendered module — re-acking init would be a lie, so it
    // reports through the host notification instead.
    if (!isForced) {
        reportInferenceOutcome();

        return;
    }

    if (isRefreshed) uwmMessageService.sendShowNotification(notificationTypes.INSIGHTS_DATA_UPDATED);
};

export default {handleExperienceInference, applyInference};
